import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Sparkles, Heart, ShoppingCart, Share2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getProductById, getSeriesBySlug } from '../data/productCatalog';

function ProductDetail({ t }) {
  const { id } = useParams();
  const { addToCart } = useCart();
  const product = getProductById(id);
  const series = product ? getSeriesBySlug(product.seriesSlug) : null;
  const thumbnails = series ? series.products.slice(0, 4) : [];
  const relatedProducts = series
    ? series.products.filter((item) => item.id !== product.id).slice(0, 4)
    : [];

  const handleAddToCart = () => {
    addToCart(product);
  };

  if (!product) {
    return (
      <div className="product-detail-page">
        <section className="product-detail-section">
          <div className="product-empty-state">
            <h1>没有找到这个产品</h1>
            <Link to="/#collection">返回系列</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <section className="product-detail-section">
        <div className="product-detail-container">
          {/* Product Images */}
          <motion.div 
            className="product-images"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="main-image">
              <img src={product.image} alt={product.name} />
            </div>
            <div className="thumbnail-grid">
              {thumbnails.map((item) => (
                <Link to={`/product/${item.id}`} key={item.id}>
                  <img src={item.image} alt={item.name} className="thumbnail" />
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div 
            className="product-info"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="product-series-label">{product.seriesName} Series</p>
            <h1 className="product-name">{product.name}</h1>
            <p className="product-code">{t.product.productCode}: {product.code}</p>
            
            <div className="product-price">
              <span className="current-price">¥ {product.price}</span>
            </div>

            <div className="product-description">
              <h3>{t.product.description}</h3>
              <p>{product.description}</p>
              <ul>
                <li><Sparkles size={16} /> {t.product.features.material}</li>
                <li><Sparkles size={16} /> {product.seriesName}系列设计语言</li>
                <li><Sparkles size={16} /> {t.product.features.craft}</li>
                <li><Sparkles size={16} /> {t.product.features.warranty}</li>
              </ul>
            </div>

            <div className="product-options">
              <div className="option-group">
                <label>{t.product.size}</label>
                <div className="size-options">
                  <button className="size-btn">6号</button>
                  <button className="size-btn active">7号</button>
                  <button className="size-btn">8号</button>
                  <button className="size-btn">9号</button>
                </div>
              </div>
            </div>

            <div className="product-actions">
              <motion.button 
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ShoppingCart size={20} />
                {t.product.addToCart}
              </motion.button>
              <motion.button 
                className="wishlist-btn"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Heart size={20} />
              </motion.button>
              <motion.button 
                className="share-btn"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Share2 size={20} />
              </motion.button>
            </div>

            <div className="product-features">
              <div className="feature-item">
                <strong>{t.product.productFeatures.shipping.title}</strong>
                <span>{t.product.productFeatures.shipping.desc}</span>
              </div>
              <div className="feature-item">
                <strong>{t.product.productFeatures.returns.title}</strong>
                <span>{t.product.productFeatures.returns.desc}</span>
              </div>
              <div className="feature-item">
                <strong>{t.product.productFeatures.authentic.title}</strong>
                <span>{t.product.productFeatures.authentic.desc}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Products */}
      <section className="related-products-section">
        <h2 className="section-title">{t.product.relatedProducts}</h2>
        <div className="related-products-grid">
          {relatedProducts.map((item, index) => (
            <motion.div
              key={item.id}
              className="related-product-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -10 }}
            >
              <Link to={`/product/${item.id}`}>
                <img src={item.image} alt={item.name} />
                <h4>{item.name}</h4>
                <p className="price">¥ {item.price}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default ProductDetail;
